import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useEffect, useRef, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { InterruptedRunCard } from './InterruptedRunCard';
import { MessageBubble } from './MessageBubble';
import { useStickToBottom } from '../hooks/useStickToBottom';
import type { EpiChatSendPayload, EpiMessage } from '../types/epi-chat-types';
import { isReviewReplyShownAsCard } from '../utils/reviewItems';

// Three Verantos-cyan dots pulsing in sequence — distinct from the
// MUI CircularProgress used for tool-card spinners so the user can
// tell at a glance which one is the agent's heartbeat vs. a tool in
// flight.
const DOT_PULSE = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
`;

const WorkingDots = () => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: 12 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: '#07b5dd',
          animation: `${DOT_PULSE} 1.2s ease-in-out infinite`,
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
  </Stack>
);

// Role-aware turn spacing. A user↔agent transition is a conversational turn
// boundary and gets the wide design gap (24px, the CONVERSATION ``gap-[24px]``
// in Figma 63:1877). Consecutive SAME-SIDE messages — most visibly an agent's
// back-to-back tool-call rows ("Worked through 1 step" ×N) — stay tight so an
// agent run reads as one block instead of N spread-out cards. ``system`` rows
// count as the agent's side.
const TURN_GAP = 6; // theme.spacing(6) = 24px
const SAME_SIDE_GAP = 1; // theme.spacing(1) = 4px — the pre-redesign default
const isUserRole = (role: EpiMessage['role']): boolean => role === 'user';

interface MessageListProps {
  messages: EpiMessage[];
  onAnswerQuestion: (answer: EpiChatSendPayload) => void;
  /** Click handler for inline artifact-ref cards — opens the drawer for that artifact. */
  onSelectArtifact?: (artifactId: string) => void;
  /**
   * Click handler for inline resource-ref cards (OR-6089). Caller injects
   * the navigate fn (typically ``useChatPreservingNavigate`` on the
   * project-page surface; plain ``useNavigate`` elsewhere). When omitted
   * the cards render but do not respond to clicks.
   */
  onNavigateResource?: (route: string) => void;
  /** OR-6504: opens a variable GROUP's details page from the family card. */
  onOpenVariableGroup?: (familyId: string) => void;
  /** Active chat session id; threaded down to memory-proposal cards. */
  sessionId?: string | null;
  emptyState?: React.ReactNode;
  /**
   * Persistent client-side element pinned to the TOP of the message stack
   * (above the first turn) whenever the thread is non-empty — used for the
   * static welcome greeting so it survives the empty → first-message
   * transition and reappears on replay. It is NEVER an entry in ``messages``,
   * so it's never streamed to or replayed from the agent (spliced from the
   * conversation history by construction). When the thread is empty the
   * greeting shows via ``emptyState`` instead.
   */
  leadingElement?: React.ReactNode;
  disabled?: boolean;
  /** When true, render an in-stream "Agent is working…" affordance below the
   *  last message so the user has a visible heartbeat during quiet periods
   *  between tool calls. */
  agentWorking?: boolean;
  /** True between subscribe-sent and ready-acked. Renders a loading
   *  affordance instead of the empty-state copy so opening an existing
   *  session never shows a blank pane while the round-trip / S3 synth is
   *  in flight. */
  sessionLoading?: boolean;
  /**
   * A brand-new chat's first message, rendered immediately from the
   * client-minted stash (see ``ChatPanelEmptyState``) — before the socket
   * even connects — so the researcher sees their own message right away
   * instead of a "Loading session…" spinner. Takes priority over
   * ``sessionLoading`` while ``messages`` is still empty; once the real
   * send lands (same id), ``messages`` becomes non-empty and this branch
   * naturally stops rendering.
   */
  pendingOptimisticMessage?: EpiMessage | null;
  /**
   * Set when the run's pod died mid-flight (multi-pod lease expiry). When
   * present and ``retryable``, an in-transcript Retry card renders below the
   * last message in place of the frozen "Agent is working…" indicator.
   */
  /** A card to show after the last message, in the flow of the chat. */
  trailingElement?: React.ReactNode;
  interruptedRun?: { runId: string; retryable: boolean } | null;
  /** Re-fire the user's last turn; wired to the Retry card's button. */
  onRetryInterruptedRun?: () => void;
  /** OR-6455: passed straight through to every `MessageBubble` — see its
   *  own doc for why. */
  userAvatarInitials?: string;
}

export const MessageList = ({
  messages,
  onAnswerQuestion,
  onSelectArtifact,
  onNavigateResource,
  onOpenVariableGroup,
  sessionId,
  emptyState,
  leadingElement,
  disabled,
  agentWorking,
  sessionLoading,
  pendingOptimisticMessage,
  trailingElement,
  interruptedRun,
  onRetryInterruptedRun,
  userAvatarInitials,
}: MessageListProps) => {
  const { scrollRef, isPinned, scrollToBottom, scrollToBottomIfPinned } = useStickToBottom();
  const reviewCardEnabled = Boolean(useFeatureFlagSplit('research_assistant_review_card'));

  // Follow streaming content: the Redux reducer returns a fresh `messages`
  // reference on every token/tool-event, so this effect fires on each
  // streaming update. We only scroll when the user is already pinned to the
  // latest message — unrelated layout changes (e.g. expanding a tool-call
  // panel) leave the `messages` reference alone and therefore don't trigger
  // a scroll.
  // A card appearing or swapping at the end of the chat counts as new content.
  const hasTrailingElement = Boolean(trailingElement);
  useEffect(() => {
    scrollToBottomIfPinned('auto');
  }, [messages, agentWorking, hasTrailingElement, scrollToBottomIfPinned]);

  // Force-scroll when the user sends a message — they just hit send, they
  // expect to see it regardless of whether the view was pinned.
  const lastUserMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser && lastUser.id !== lastUserMessageIdRef.current) {
      lastUserMessageIdRef.current = lastUser.id;
      scrollToBottom('smooth');
    }
  }, [messages, scrollToBottom]);

  // Count new messages that arrived while the user was scrolled up.
  const [unreadCount, setUnreadCount] = useState(0);
  const prevCountRef = useRef(messages.length);
  useEffect(() => {
    const added = messages.length - prevCountRef.current;
    prevCountRef.current = messages.length;
    if (added > 0 && !isPinned) {
      setUnreadCount((n) => n + added);
    }
  }, [messages.length, isPinned]);
  useEffect(() => {
    if (isPinned) setUnreadCount(0);
  }, [isPinned]);

  // Render-only substitute for ``messages`` while a chat has no real
  // messages yet but a first-message optimistic bubble is pending — lets it
  // flow through the SAME top-anchored Stack layout real messages use
  // (below) instead of the bottom-anchored empty-state Box, which would
  // otherwise visibly jump position once the real message replaces it.
  // Deliberately NOT used in the scroll/unread-tracking effects above: those
  // key off the real ``messages`` array, and this substitute always has at
  // most one entry with no streaming/scroll concerns.
  const displayMessages =
    messages.length > 0 ? messages : pendingOptimisticMessage ? [pendingOptimisticMessage] : [];

  // The outer Box owns the scroll; the inner Stack flows top-down like any
  // ordinary chat thread. (A prior `marginTop: auto` bottom-pinned short
  // conversations against the composer — but the persistent `leadingElement`
  // greeting is the first row inside this same Stack, so its position ended
  // up a function of how much message content followed it: a short chat left
  // a large, inconsistent gap above the greeting; a long one left almost
  // none. Top-anchoring fixes the greeting at a constant offset below the
  // header regardless of conversation length — any leftover space simply
  // falls below the last message, which is the normal, expected chat look.)
  return (
    <Box
      sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          // Belt-and-braces: any rogue child wider than the column gets
          // clipped here rather than scrolling the whole layout sideways
          // and shifting prose under the left rail. The Markdown renderer
          // already breaks long tokens; this catches anything that slips
          // through (raw <pre> blocks, embedded SVGs, etc.).
          overflowX: 'hidden',
          minWidth: 0,
          // 24px chat gutter (OR-6123) — matches the project page gutter.
          paddingX: 6,
          paddingTop: 3,
          paddingBottom: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Reading-width cap: prose stays in a comfortable column even when
            the surrounding layout is wide or the SQL drawer narrows the
            chat column. Empty state and message list share the same cap so
            the welcome message and turn rhythm align horizontally. */}
        {displayMessages.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              paddingBottom: 4,
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 760 }}>
              {sessionLoading ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ color: 'text.secondary' }}
                  data-testid="epi-chat-session-loading"
                >
                  <CircularProgress size={14} thickness={5} />
                  <Typography variant="body2">Loading session…</Typography>
                </Stack>
              ) : (
                emptyState ?? (
                  <Typography variant="body2" color="text.secondary" align="center">
                    Ask the agent about a study you want to build.
                  </Typography>
                )
              )}
            </Box>
          </Box>
        ) : (
          <Stack
            // Per-message top margins carry the turn spacing (see ``TURN_GAP`` /
            // ``SAME_SIDE_GAP``), so a uniform Stack gap is wrong here — keep it 0.
            spacing={0}
            sx={{ width: '100%', maxWidth: 760 }}
          >
            {/* Static welcome greeting (client-side only) pinned above the
                first turn — see ``leadingElement`` prop doc. Not part of
                ``messages``, so the agent never sees it. */}
            {leadingElement}
            {displayMessages.map((message, index) => {
              // The Review card's reply repeats the summary card on the message before it.
              // TODO: cleanup with research_assistant_review_card ff
              if (reviewCardEnabled && isReviewReplyShownAsCard(displayMessages, index))
                return null;
              // Gap to the previous message: wide across a user↔agent turn
              // boundary, tight between same-side messages (an agent run). The
              // pinned welcome greeting counts as an agent turn for message 0;
              // with no welcome, message 0 has no preceding row so no margin.
              const prevIsUser = index === 0 ? false : isUserRole(displayMessages[index - 1].role);
              const marginTop =
                index === 0 && !leadingElement
                  ? 0
                  : isUserRole(message.role) === prevIsUser
                  ? SAME_SIDE_GAP
                  : TURN_GAP;
              // OR-6290 (AV-14): the intent checkpoint's inline Approve button
              // (flag-ON) shows only while its message is the latest in the
              // transcript. Compute that here — approving appends an optimistic
              // user turn (and typing a redirect appends the user's message), so
              // the checkpoint stops being last and the button disappears.
              const isLast = index === displayMessages.length - 1;
              return (
                <Box key={message.id} sx={{ marginTop }}>
                  <MessageBubble
                    message={message}
                    onAnswerQuestion={onAnswerQuestion}
                    onNavigateResource={onNavigateResource}
                    onOpenVariableGroup={onOpenVariableGroup}
                    onSelectArtifact={onSelectArtifact}
                    sessionId={sessionId}
                    isLast={isLast}
                    disabled={disabled}
                    userAvatarInitials={userAvatarInitials}
                  />
                </Box>
              );
            })}
            {agentWorking && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  color: 'text.secondary',
                  paddingY: 0.5,
                  // Continuation of the agent's turn → tight; if the last
                  // message was the user's, the agent is just starting → wide.
                  // OR-6348: keyed off ``displayMessages`` (not ``messages``) —
                  // while a brand-new chat's first send is still pending, the
                  // real array is empty and the optimistic bubble substitutes
                  // for it (see ``displayMessages`` above); using ``messages``
                  // here made this margin jump from 0 to TURN_GAP the instant
                  // the real message replaced the pending one, even though the
                  // visible last row (the user's bubble) never changed.
                  marginTop:
                    displayMessages.length === 0
                      ? 0
                      : isUserRole(displayMessages[displayMessages.length - 1].role)
                      ? TURN_GAP
                      : SAME_SIDE_GAP,
                }}
                aria-live="polite"
                data-testid="epi-chat-working-indicator"
              >
                <WorkingDots />
                <Typography variant="caption">Research Assistant is working…</Typography>
              </Stack>
            )}
            {trailingElement && <Box sx={{ marginTop: SAME_SIDE_GAP }}>{trailingElement}</Box>}
          </Stack>
        )}
        {/* Render the Retry card regardless of message count. A run that dies
            before persisting any message yields an empty cold snapshot, and the
            empty-state branch above would otherwise hide the card even though
            ``interruptedRun`` is set. */}
        {interruptedRun?.retryable && onRetryInterruptedRun && (
          <InterruptedRunCard onRetry={onRetryInterruptedRun} disabled={disabled} />
        )}
      </Box>
      {!isPinned && unreadCount > 0 && (
        <Button
          variant="contained"
          size="small"
          startIcon={<KeyboardArrowDownIcon />}
          onClick={() => scrollToBottom('smooth')}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            borderRadius: 999,
            textTransform: 'none',
            boxShadow: 3,
          }}
        >
          {unreadCount === 1 ? '1 new message' : `${unreadCount} new messages`}
        </Button>
      )}
    </Box>
  );
};
